import { useEffect, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { styled } from "@pigment-css/react";
import { ChevronDown } from "@acto/web-common/components";
import { useTranslations } from "@acto/web-common/locales";
import { useDebouncedSearchTerm } from "@acto/taskpane/core/utils/hooks/useDebouncedSearchTerm";
import { useManagedEmployees } from "@acto/taskpane/core/data-access/team/useManagedEmployees";
import { usePopover } from "../primitives/usePopover";
import { CheckMark, FilterPillTrigger, ItemLabel, SelectItem, SelectPopover } from "../primitives/selectParts";

const SCROLL_THRESHOLD = 100;

const Popover = styled(SelectPopover)(() => ({
  minWidth: "260px",
  padding: 0,
  display: "flex",
  flexDirection: "column",
}));

const SearchInput = styled("input")(({ theme }) => ({
  width: "100%",
  boxSizing: "border-box",
  padding: `${theme.managementView.spacing[12]} ${theme.managementView.spacing[16]}`,
  fontFamily: theme.managementView.font.familySecondary,
  fontSize: theme.managementView.font.size[15],
  fontWeight: theme.managementView.font.weight[440],
  letterSpacing: theme.managementView.font.letterSpacing.tight,
  color: theme.managementView.colors.neutral[90],
  backgroundColor: "transparent",
  border: "none",
  borderBottom: `1px solid ${theme.managementView.colors.neutral[8]}`,
  outline: "none",

  "&::placeholder": {
    color: theme.managementView.colors.neutral[56],
  },
}));

const ScrollContainer = styled("div")(() => ({
  maxHeight: "260px",
  overflowY: "auto",
  scrollbarWidth: "thin",
  scrollbarColor: "transparent transparent",

  "&:hover": {
    scrollbarColor: "rgba(19, 19, 19, 0.32) transparent",
  },
}));

const ItemsContainer = styled("div")(({ theme }) => ({
  padding: theme.managementView.spacing[4],
}));

const EmptyMessage = styled("div")(({ theme }) => ({
  padding: theme.managementView.spacing[16],
  textAlign: "center",
  fontFamily: theme.managementView.font.familySecondary,
  fontSize: theme.managementView.font.size[14],
  color: theme.managementView.colors.neutral[56],
}));

interface EmployeeFilterProps {
  value: string | null;
  onChange: (employeeId: string | null) => void;
  disabled?: boolean;
}

export const EmployeeFilter = ({ value, onChange, disabled }: EmployeeFilterProps) => {
  const t = useTranslations("customers.filters");
  const { isOpen, position, triggerRef, popoverRef, toggle, close } = usePopover();
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const {
    displayValue: searchValue,
    setDisplayValue: setSearchValue,
    debouncedValue: debouncedSearch,
  } = useDebouncedSearchTerm();

  const {
    data: employees,
    isFetching,
    isPending,
    hasNextPage,
    fetchNextPage,
  } = useManagedEmployees({ searchTerm: debouncedSearch });

  const placeholderLabel = t("employeePlaceholder");

  // Keeps the trigger pill stable when the popover search filters the selected id out of `employees`.
  const lastKnownSelectionRef = useRef<{ id: string; name: string } | null>(null);

  useEffect(() => {
    if (!value || !employees) return;

    const found = employees.find((e) => e.id === value);
    if (found) {
      lastKnownSelectionRef.current = { id: found.id, name: found.name };
    }
  }, [employees, value]);

  const selectedName = useMemo(() => {
    if (!value) return placeholderLabel;

    const found = employees?.find((e) => e.id === value);
    if (found) return found.name;

    if (lastKnownSelectionRef.current?.id === value) {
      return lastKnownSelectionRef.current.name;
    }

    return placeholderLabel;
  }, [employees, value, placeholderLabel]);

  useEffect(() => {
    if (!isOpen) setSearchValue("");
  }, [isOpen, setSearchValue]);

  const handleSelect = (id: string | null) => {
    onChange(id);
    setSearchValue("");
    close();
  };

  const handleScroll = () => {
    if (!scrollContainerRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
    if (distanceFromBottom < SCROLL_THRESHOLD && hasNextPage && !isFetching) {
      fetchNextPage();
    }
  };

  const triggerLabel = value ? selectedName : placeholderLabel;
  const isLoading = isPending || (isFetching && (employees?.length ?? 0) === 0);

  return (
    <>
      <FilterPillTrigger ref={triggerRef} type="button" disabled={disabled} onClick={toggle}>
        {triggerLabel}
        <ChevronDown />
      </FilterPillTrigger>
      {isOpen &&
        createPortal(
          <Popover ref={popoverRef} style={position}>
            <SearchInput
              type="text"
              placeholder={t("employeeSearchPlaceholder")}
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              autoFocus
            />
            <ScrollContainer ref={scrollContainerRef} onScroll={handleScroll}>
              <ItemsContainer>
                <SelectItem onClick={() => handleSelect(null)}>
                  <ItemLabel>{t("employeeAll")}</ItemLabel>
                  {value == null && <CheckMark />}
                </SelectItem>
                {(employees ?? []).map((employee) => (
                  <SelectItem key={employee.id} onClick={() => handleSelect(employee.id)}>
                    <ItemLabel>{employee.name}</ItemLabel>
                    {value === employee.id && <CheckMark />}
                  </SelectItem>
                ))}
                {!isLoading && (employees?.length ?? 0) === 0 && <EmptyMessage>{t("employeeEmpty")}</EmptyMessage>}
              </ItemsContainer>
            </ScrollContainer>
          </Popover>,
          document.body,
        )}
    </>
  );
};
